import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RouteMeta } from '../types/route';
import type { User } from '../stores/slices/authSlice';
import { redirectToHomeLogin, redirectToHomePage } from '../utils/auth-redirect';
import { isPlatformOwner, checkRoles, checkPermissions } from '../hooks/usePermission';

/**
 * 路由守卫配置
 */
export interface RouteGuardProps {
  children: ReactNode;
  meta?: RouteMeta;
  isAuthenticated: boolean;
  /** 当前用户（当 meta.requiresPlatformOwner 为 true 时必传，用于平台所有者校验） */
  user?: User | null;
  trpc: any;
  dispatch: any;
  setCredentials: any;
  isHomeApp?: boolean;
}

/**
 * 路由守卫
 * 
 * 逻辑：
 * 1. requiresAuth: false → 直接渲染（公开路由，如登录页）
 * 2. requiresAuth: true + 已登录 → 直接渲染
 * 3. requiresAuth: true + 未登录 → 检查一次 Cookie，失败则跳转登录页
 */
export function RouteGuard({
  children,
  meta = {},
  isAuthenticated,
  user = null,
  trpc,
  dispatch,
  setCredentials,
  isHomeApp = false,
}: RouteGuardProps) {
  const navigate = useNavigate();
  const { requiresAuth = true, requiresPlatformOwner = false, requiredRoles, requiredPermissions } = meta;
  const [hasChecked, setHasChecked] = useState(false);
  const hasRedirectedForbidden = useRef(false);

  // 在 useEffect 中执行 /forbidden 跳转，用 ref 保证只跳一次，避免渲染中 setState 导致无限循环
  const needsForbiddenRedirect =
    isAuthenticated &&
    (() => {
      if (requiredRoles?.length && (user == null || !checkRoles(user, requiredRoles))) return true;
      if (requiredPermissions?.length && (user == null || !checkPermissions(user, requiredPermissions))) return true;
      if (requiresPlatformOwner && !isPlatformOwner(user)) return true;
      return false;
    })();

  useEffect(() => {
    if (needsForbiddenRedirect && !hasRedirectedForbidden.current) {
      hasRedirectedForbidden.current = true;
      navigate('/forbidden', { replace: true });
    }
  }, [needsForbiddenRedirect, navigate]);

  // ✅ Hooks 必须始终调用（放在所有条件判断之前）
  // 注意：认证状态的更新由 AuthProvider 在全局完成，RouteGuard 只负责路由守卫
  // 只有 requiresAuth === true 且未登录时，才需要检查 Cookie（用于判断是否跳转）
  const shouldCheckAuth = requiresAuth && !isAuthenticated && !hasChecked;
  
  const { data: userInfo, isLoading, error } = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: shouldCheckAuth, // 只在需要时执行查询
  });
  
  useEffect(() => {
    if (!shouldCheckAuth || hasChecked || isLoading) return;
    
    if (userInfo) {
      // Cookie 有效，更新 Redux
      dispatch(setCredentials({
        user: {
          id: userInfo.id,
          username: userInfo.username,
          nickname: userInfo.nickname ?? undefined,
          email: userInfo.email ?? undefined,
          mobile: userInfo.mobile ?? undefined,
          avatarUrl: userInfo.avatarUrl ?? undefined,
          status: userInfo.status ?? undefined,
          type: (userInfo as any).type ?? undefined,
          tenantId: typeof userInfo.tenantId === 'string' ? userInfo.tenantId : undefined,
          permissions: (userInfo as any).permissions ?? undefined,
          roles: (userInfo as any).roles ?? undefined,
          apps: (userInfo as any).apps ?? undefined,
          spaces: (userInfo as any).spaces ?? undefined,
        },
        token: '',
      }));
    }
    setHasChecked(true);
  }, [shouldCheckAuth, hasChecked, isLoading, userInfo, error, dispatch, setCredentials]);
  
  // 1️⃣ 公开路由（requiresAuth === false）：立即渲染，不检查任何东西
  // 认证状态的更新由 AuthProvider 在后台完成，不影响路由渲染
  if (requiresAuth === false) {
    return <>{children}</>;
  }
  
  // 2️⃣ 已登录：角色/权限校验，不通过则在 useEffect 中跳转 Forbidden 页，本帧先返回 null
  if (isAuthenticated) {
    if (needsForbiddenRedirect) {
      return null;
    }
    return <>{children}</>;
  }
  
  // 3️⃣ 正在检查认证（静默检查，不显示任何提示）
  if (isLoading || !hasChecked) {
    return null;
  }
  
  // 4️⃣ 检查完成，确认未登录
  if (hasChecked && !isAuthenticated) {
    if (isHomeApp) {
      // ✅ home 应用：不跳转，直接渲染子组件（让页面自己决定显示什么，如显示登录按钮）
      return <>{children}</>;
    } else {
      // ✅ 其他应用（如 admin）：必须跳转到登录页
      // 注意：如果正在退出登录（useLogout 会先跳转），这里不应该执行
      // 但为了保险起见，我们仍然执行跳转（如果 useLogout 没有跳转）
      console.log('🔄 [RouteGuard] 检测到未登录，跳转到登录页');
      redirectToHomeLogin(true);
      return null;
    }
  }
  
  return <>{children}</>;
}
